import { createEffect, createSignal, untrack } from "solid-js"
import { CatmullRomCurve3, Group, type Scene, Vector3 } from "three"
import { onCleanupRemoveAndDispose } from "../../../../rx/cleanup/onCleanupRemoveAndDispose"
import { createSharedTileGraphNodeCollectionMemo } from "../../../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import useGridWidthHeight from "../../../../rx/memo/useGridWidthHeight"
import { useNodeNeighborBaseWaterCurrentReactiveMap } from "../../../../rx/memo/useNodeNeighborBaseWaterCurrentReactiveMap"
import { useSharedRiverExitEntsListMemo } from "../../../../rx/memo/useSharedRiverExitEntsListMemo"
import { useSharedWaterEntsWithCurrentListMemo } from "../../../../rx/memo/useSharedWaterEntsWithCurrentListMemo"
import { useSharedWaterFlowNodeMapMemo } from "../../../../rx/memo/useSharedWaterFlowNodeMapMemo"
import isHasFromToHasIdListSame from "../../../../rx/signal/equals/isHasFromToHasIdListSame"
import dijkstraDirectionCharsLowerCase from "../../../core/direction/dijkstraDirectionCharsLowerCase"
import type { Engine } from "../../../core/engine/Engine.type"
import type { RiverEntity } from "../../../core/entity/EntityType/River"
import type { SeaEntity } from "../../../core/entity/EntityType/Sea"
import { isEntitySea } from "../../../core/entity/entityTypeMetaList.generated"
import rotatePlaneToLayFlat from "../../fn/rotatePlaneToLayFlat"
import appendTerrainMesh1EstuaryParticleCurves from "./appendTerrainMesh1EstuaryParticleCurves"
import appendTerrainMesh1WaterFlowParticleCurvesV2 from "./appendTerrainMesh1WaterFlowParticleCurvesV2"
import { createGPUDrivenEstuaryParticles } from "./createGPUDrivenEstuaryParticles"
import { createWaterCurveParticleDataFromCurves } from "./createWaterCurveParticleDataFromCurves"

export type TerrainMesh1EstuaryTilePositionEdge = {
  from: RiverEntity,
  to: SeaEntity,
}

const SHOW_PARTICLES = true

export default function createTerrainMesh1EstuaryEffect(
  engine: Engine,
  scene: Scene,
): void {
  // console.log('createTerrainMesh1EstuaryEffect.init')
  const getTileGraphNodesList = createSharedTileGraphNodeCollectionMemo(engine)
  const getRiverExitEntsList = useSharedRiverExitEntsListMemo(engine)
  const getWaterEntsWithCurrentList = useSharedWaterEntsWithCurrentListMemo(engine)
  const waterFlowNodeMap = useSharedWaterFlowNodeMapMemo(engine)
  const nodeNeighborBaseWaterCurrentReactiveMap = useNodeNeighborBaseWaterCurrentReactiveMap(engine)

  const getGridSize = useGridWidthHeight(engine)

  createEffect(() => {
    // console.log('createTerrainMesh1EstuaryEffect.effect')
    const { width: gridWidth } = getGridSize()
    const tpNodeList = getTileGraphNodesList()

    const [getEdges, setEdges] = createSignal<Array<TerrainMesh1EstuaryTilePositionEdge>>([], {
      equals: isHasFromToHasIdListSame,
    })

    createEffect(() => {
      const edges: Array<TerrainMesh1EstuaryTilePositionEdge> = []
      const riverExitEntsList = getRiverExitEntsList()
      for (const riverExitEnt of riverExitEntsList) {
        const from = riverExitEnt
        const nodeIndex0 = riverExitEnt.x + riverExitEnt.y * gridWidth
        const node0 = tpNodeList[nodeIndex0]!
        for (const dirChar of dijkstraDirectionCharsLowerCase) {
          const node1 = node0[dirChar]
          if (node1) {
            const to = node1.ents().find(isEntitySea)
            if (to) {
              edges.push({ from, to })
            }
          }
        }
      }
      setEdges(edges)
    })
    // createEffect(() => {
    //   console.log('createTerrainMesh1EstuaryEffect.effect.edges', getEdges().length)
    // })
    createEffect(() => {
      // console.log('createTerrainMesh1EstuaryEffect.effect')

      const group = new Group()
      const edges = getEdges()
      const entsWithCurrentList = getWaterEntsWithCurrentList()
      untrack(() => {

        const particleCurves: Array<CatmullRomCurve3> = []
        const particleBoundingBoxPoints: Array<Vector3> = []

        appendTerrainMesh1EstuaryParticleCurves(edges, particleCurves, particleBoundingBoxPoints, group)
        // appendTerrainMesh1WaterFlowParticleCurvesV1(entsWithCurrentList, waterFlowNodeMap, particleCurves, particleBoundingBoxPoints, group, gridWidth)
        appendTerrainMesh1WaterFlowParticleCurvesV2(entsWithCurrentList, waterFlowNodeMap,
          nodeNeighborBaseWaterCurrentReactiveMap,
          particleCurves, particleBoundingBoxPoints, group, gridWidth)

        // const curveT: CatmullRomCurve3 = new CatmullRomCurve3([
        //   new Vector3(0, 0, 2),
        //   new Vector3(4, -4, 2),
        //   new Vector3(8, -0, 2),
        // ])
        // const points = curveT.getSpacedPoints(3)
        // if (SHOW_DEBUG_MARKERS) {
        //   const geometry = new BufferGeometry().setFromPoints(points)
        //   const material = new LineBasicMaterial({ color: 0x00ff00 })
        //   const curveObject = new Line(geometry, material)
        //   group.add(curveObject)
        //   // curveObject.visible = false
        // }
        // particleCurves.push(curveT)
        // particleBoundingBoxPoints.push(...points)

        if (SHOW_PARTICLES) {
          if (particleCurves.length > 0) {
            const particleData = createWaterCurveParticleDataFromCurves(particleCurves)
            const particleMesh = createGPUDrivenEstuaryParticles(engine, particleData, particleBoundingBoxPoints)
            group.add(particleMesh)
          }
        }

        rotatePlaneToLayFlat(group)
        group.position.y -= 1
        scene.add(group)
        // group.visible = false
      })
      onCleanupRemoveAndDispose(group)
    })
  })
}